import { SerializedError } from '@reduxjs/toolkit';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from 'components';
import youtubeChannelApi from 'services/api/youtubeChannel';
import { useSubscription } from 'services/hooks';
import ChannelURL from '../ChannelURL';
import ConfirmationDialog from '../ConfirmationDialog';
import StatusMessage from '../StatusMessage';
import { StyledFormWrapper } from './styled';
import useChannelValidation from './useChannelValidation';

export const InputForm = () => {
  const { t } = useTranslation();
  const { isFreeAccount } = useSubscription();
  const validate = useChannelValidation();

  const [channelName, setChannelName] = useState('');
  const [validationError, setValidationError] =
    useState<SerializedError | null>(null);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);

  const [addChannel, { isLoading, error: networkError, reset }] =
    youtubeChannelApi.useAddChannelMutation();

  const closeConfirmationDialog = () => setIsConfirmationDialogOpen(false);

  const handleChannelNameChange = useCallback(
    (name: string) => {
      setChannelName(name);
      setValidationError(null);
      reset();
    },
    [reset, setChannelName]
  );

  const handleChannelNameSubmit = useCallback(() => {
    const isValid = validate(channelName);
    if (!isValid) {
      const error = new Error(
        t('account:youtubeChannel:whitelistContainer:statusMessage:invalidURL')
      );
      setValidationError(error);
      return;
    }
    setIsConfirmationDialogOpen(true);
  }, [channelName, t, validate]);

  const handleChannelNameConfirmation = useCallback(() => {
    closeConfirmationDialog();
    if (!channelName) {
      return;
    }
    addChannel(channelName);
  }, [channelName, addChannel]);

  const error = (validationError || networkError) as SerializedError | null;

  const isSubmitButtonDisabled =
    !channelName || isFreeAccount || isLoading || !!error;

  return (
    <>
      <StyledFormWrapper>
        <ChannelURL
          disabled={isLoading}
          error={error}
          onChange={handleChannelNameChange}
        />

        <LoadingButton
          variant="contained"
          disabled={isSubmitButtonDisabled}
          onClick={handleChannelNameSubmit}
        >
          {t('account:youtubeChannel:whitelistContainer:submit')}
        </LoadingButton>
      </StyledFormWrapper>

      {error && <StatusMessage error={error.message} />}

      {channelName && (
        <ConfirmationDialog
          isOpen={isConfirmationDialogOpen}
          onSubmit={handleChannelNameConfirmation}
          onClose={closeConfirmationDialog}
        />
      )}
    </>
  );
};

export default InputForm;
